import { X, Gift, Printer, MapPin, CheckCircle2 } from "lucide-react";
import { TIME_SLOTS, humanSlot } from "@/data/collections";
import { StatusPill, DepositPill, DonePill } from "@/components/AdminDetails";

export const DayPanel = ({ date, bookings, onClose, onSelect, onToggle }) => {
  const d = new Date(date + "T12:00:00");
  const bySlot = {};
  for (const b of bookings) (bySlot[b.time_slot] ||= []).push(b);
  const isSunday = d.getDay() === 0;

  return (
    <aside className="border" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream-card)" }} data-testid="admin-day-panel">
      <div className="p-5 border-b flex items-start justify-between gap-4" style={{ borderColor: "var(--pp-border)" }}>
        <div>
          <div className="pp-caption mb-1">{d.toLocaleDateString(undefined, { weekday: "long" })}</div>
          <h2 className="font-serif-display text-3xl" style={{ color: "var(--pp-burgundy)" }} data-testid="admin-day-title">
            {d.toLocaleDateString(undefined, { month: "long", day: "numeric" })}
          </h2>
          <div className="text-xs mt-1" style={{ color: "var(--pp-text-muted)" }}>
            {bookings.length} appointment{bookings.length === 1 ? "" : "s"}{isSunday ? " · Closed Sundays" : ""}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <a href={`/admin/print?date=${date}`} target="_blank" rel="noreferrer" data-testid="admin-day-print" title="Print day sheet"
            className="h-9 px-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest hover:bg-[var(--pp-cream)]" style={{ color: "var(--pp-terracotta)" }}>
            <Printer className="h-4 w-4" /> Print
          </a>
          <button onClick={onClose} aria-label="Close day" data-testid="admin-day-close" className="h-9 w-9 inline-flex items-center justify-center hover:bg-[var(--pp-cream)]">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <ul className="divide-y" style={{ borderColor: "var(--pp-border)" }}>
        {TIME_SLOTS.map((slot) => {
          const items = bySlot[slot] || [];
          return (
            <li key={slot} className="p-4 grid grid-cols-[110px,1fr] gap-4" style={{ borderColor: "var(--pp-border)" }} data-testid={`admin-day-slot-${slot.replace(":", "")}`}>
              <div className="font-serif-display text-xl pt-1" style={{ color: items.length ? "var(--pp-burgundy)" : "var(--pp-text-muted)" }}>{humanSlot(slot)}</div>
              <div className="space-y-2">
                {items.length === 0 ? (
                  <div className="text-xs italic pt-2" style={{ color: "var(--pp-text-muted)" }}>Open</div>
                ) : items.map((b) => (
                  <div key={b.id} role="button" tabIndex={0} onClick={() => onSelect(b)} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && onSelect(b)} data-testid={`admin-day-appt-${b.reference}`}
                    className="w-full text-left p-3 border transition-colors hover:bg-[var(--pp-cream)] cursor-pointer" style={{ borderColor: "var(--pp-border)", backgroundColor: "var(--pp-cream)" }}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium flex items-center gap-2" style={{ color: "var(--pp-text)" }}>
                        {b.full_name}{b.is_gift && <Gift className="h-3.5 w-3.5" style={{ color: "var(--pp-terracotta)" }} />}
                      </div>
                      <div className="flex items-center gap-1.5">{b.completed && <DonePill />}<DepositPill paid={b.deposit_paid} /><StatusPill status={b.status} /></div>
                    </div>
                    <div className="text-xs mt-1" style={{ color: "var(--pp-text-muted)" }}>
                      {b.collection_name} · {b.size_label} · {b.reference}
                    </div>
                    <div className="text-xs truncate" style={{ color: "var(--pp-text-muted)" }}>{b.address}</div>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.address)}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      data-testid={`admin-day-map-${b.reference}`} className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest underline underline-offset-2" style={{ color: "var(--pp-terracotta)" }}>
                      <MapPin className="h-3 w-3" /> Open in Maps
                    </a>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(b.id, { completed: !b.completed }); }}
                      data-testid={`admin-day-complete-${b.reference}`} className="mt-1 ml-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest underline underline-offset-2"
                      style={{ color: b.completed ? "var(--pp-terracotta)" : "var(--pp-burgundy)" }}>
                      <CheckCircle2 className="h-3 w-3" /> {b.completed ? "Reopen" : "Mark complete"}
                    </button>
                  </div>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
};
